import type { ProductSlugs } from '@deque/billing-utils';
import { Loader, Panel, PanelContent } from '@deque/cauldron-react';
import { IssueFieldSource, JiraFieldMappers } from '@deque/ui-export-jira';
import classNames from 'classnames';
import type { TFunction } from 'i18next';
import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';

import { useFormContext } from 'react-hook-form';
import {
  type FieldMappingField,
  IMPACTS,
  type Impacts,
  type StepMap,
  type TemplateFormFields,
  type TemplateFormFieldsKeys
} from '~/app/types';
import analyticsInstances, {
  getFallbackAnalyticsInstanceId
} from '~/common/analyticsInstances';
import {
  type IntegrationTemplate,
  createIntegrationTemplate,
  updateIntegrationTemplate
} from '~/common/api-client';
import type { SupportedIntegrationProductSlugs } from '~/common/constants';
import { useGlobalToast } from '~/common/contexts/globalToast';
import useMediaQuery from '~/common/hooks/useMediaQuery';
import { getIntegrationProductInfoFromSlug } from '~/common/utils/get-integration-product-name-from-slug';
import { AxeIssueMappingSchema } from '~/common/utils/integrations-client/jira/fieldMapping/MappingSchema';
import { findJiraFieldByKey } from '~/common/utils/integrations-client/jira/fieldMapping/findJiraFieldByKey';
import type { IssueTypeField } from '~/common/utils/integrations-client/jira/getIssueTypeFields';
import type { ProjectsAndIssueTypes } from '~/common/utils/integrations-client/jira/getProjectsAndIssueTypes';
import { ExcludedIntegrationFields } from '../Steps/FieldMapping/FieldMapping';
import TemplateWizardFooter from './FormFooter/FormFooter';
import { useFieldsContext } from './IssueTypeFieldsContext';
import styles from './TemplateWizardForm.css';

export interface TemplateWizardFormProps {
  initialTemplate?: IntegrationTemplate;
  integrationProductSlug:
    | SupportedIntegrationProductSlugs
    | ProductSlugs.axeAuditorIntegration;
  connectionId: string;
  token: string;
  enterpriseId: string;
  currentStep: number;
  steps: StepMap[];
  setCurrentStep: Dispatch<SetStateAction<number>>;
  isStepDataLoading: boolean;
  templates: IntegrationTemplate[];
  refreshTemplates: () => void;
  projects: ProjectsAndIssueTypes[];
  isStepDataError: boolean;
}

const defaultImpactMapping = IMPACTS.reduce(
  (acc, impact) => ({ ...acc, [impact]: '' }),
  {} as Record<Impacts, string>
);

export const defaultTemplateValues: TemplateFormFields = {
  templateName: '',
  project: '',
  issueType: '',
  attachScreenshot: true,
  fieldMapping: [],
  impactMapping: defaultImpactMapping,
  customLabels: [],
  summaryPrefix: ''
};

const mappers = Object.values(JiraFieldMappers);

const mapTemplateDataForFrom = (
  template: IntegrationTemplate,
  fields: IssueTypeField[]
): TemplateFormFields => {
  const {
    name,
    project_id,
    issue_type_id,
    field_mappings,
    impact_mappings,
    attach_screenshot,
    summary_prefix
  } = template;

  const formattedFieldMapping =
    field_mappings && fields.length
      ? AxeIssueMappingSchema.read(field_mappings, fields).mapping
      : {};

  const fieldMapping = Object.entries(formattedFieldMapping).map(
    ([key, { source, sourceType }]) => ({
      axeField: source,
      mappingField: key,
      sourceType
    })
  );

  const impactMapping = impact_mappings
    ? { ...defaultTemplateValues.impactMapping, ...impact_mappings }
    : defaultTemplateValues.impactMapping;

  return {
    ...defaultTemplateValues,
    templateName: name,
    project: project_id || '',
    attachScreenshot: attach_screenshot,
    issueType: issue_type_id || '',
    fieldMapping,
    impactMapping,
    summaryPrefix: summary_prefix || ''
  };
};

export const getTemplateSchema = (t: TFunction) =>
  z.object({
    templateName: z
      .string({
        required_error: t('Template Name is required')
      })
      .trim()
      .min(1, {
        message: t('Template Name is required')
      }),
    project: z
      .string({
        required_error: t('Project is required')
      })
      .min(1, {
        message: t('Project is required')
      }),
    issueType: z
      .string({
        required_error: t('Issue Type is required')
      })
      .min(1, {
        message: t('Issue Type is required')
      }),
    attachScreenshot: z.boolean(),
    fieldMapping: z.array(
      z.object({
        axeField: z.string().optional(),
        mappingField: z.string().optional(),
        sourceType: z.string().optional()
      })
    ),
    summaryPrefix: z
      .string()
      .max(100, {
        message: t('Summary prefix must be less than 100 characters')
      })
      .optional(),
    impactMapping: z.record(
      z.enum(IMPACTS as [string, ...string[]]),
      z.string().optional()
    )
    //for future implementation, out of scope for now
    // customLabels: z.array(z.record(z.string(), z.string()))
  });

const filterRequiredFieldsWithoutDefault = (
  fields: IssueTypeField[],
  integrationProductSlug: SupportedIntegrationProductSlugs
) => {
  return fields.filter(
    ({ key, required, hasDefaultValue }) =>
      required &&
      !hasDefaultValue &&
      !ExcludedIntegrationFields[integrationProductSlug].includes(key)
  );
};

const TemplateWizardForm = ({
  initialTemplate,
  integrationProductSlug,
  connectionId,
  token,
  enterpriseId,
  currentStep,
  steps,
  setCurrentStep,
  templates,
  isStepDataLoading,
  refreshTemplates,
  projects,
  isStepDataError
}: TemplateWizardFormProps) => {
  const narrow = useMediaQuery('(max-width: 37.5rem)');
  const history = useHistory();
  const { t } = useTranslation();
  const { setContents } = useGlobalToast();

  const analytics =
    analyticsInstances[getFallbackAnalyticsInstanceId(integrationProductSlug)];

  const { handleSubmit, getValues, reset, formState, setFocus } =
    useFormContext();

  const { data: issueMappingFields } = useFieldsContext();

  const [isLoading, setIsLoading] = useState(false);

  const formRefHandler = useCallback(
    (element: HTMLFormElement | null) => {
      if (!element || isLoading || isStepDataLoading) {
        return;
      }

      const focusableElements = Array.from(
        element.querySelectorAll(
          'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a'
        )
      ) as HTMLElement[];

      focusableElements[0]?.focus();
    },
    [isLoading, isStepDataLoading]
  );

  useEffect(() => {
    const { project: projectFieldValue } = getValues();

    if (!initialTemplate || !issueMappingFields || !!projectFieldValue) {
      return;
    }

    try {
      const initialValues = mapTemplateDataForFrom(
        initialTemplate,
        issueMappingFields
      );

      const requiredFieldsWithoutDefault = filterRequiredFieldsWithoutDefault(
        issueMappingFields,
        integrationProductSlug as SupportedIntegrationProductSlugs
      );

      const formattedMappedFields = initialValues.fieldMapping.map(
        field => field.mappingField
      );
      const initialTemplateMappedFields =
        initialTemplate.field_mappings &&
        AxeIssueMappingSchema.isV2(initialTemplate.field_mappings)
          ? Object.keys(initialTemplate.field_mappings.mapping)
          : Object.keys(initialTemplate.field_mappings || []);

      // when Jira fields were updated and some required fields without default value were added
      const missedFields = requiredFieldsWithoutDefault.filter(
        field => !initialTemplateMappedFields.includes(field.key)
      );

      let warning = '';

      const prevSchemaFields =
        initialTemplate.field_mappings &&
        AxeIssueMappingSchema.isV2(initialTemplate.field_mappings)
          ? initialTemplate.field_mappings.fields
          : [];

      const prevRequiredFieldsWithoutDefault =
        filterRequiredFieldsWithoutDefault(
          prevSchemaFields,
          integrationProductSlug as SupportedIntegrationProductSlugs
        ).map(field => field.key);

      const newlyAddedRequiredFieldsWithoutDefault =
        requiredFieldsWithoutDefault.filter(
          field => !prevRequiredFieldsWithoutDefault.includes(field.key)
        );

      if (newlyAddedRequiredFieldsWithoutDefault.length) {
        warning = t(
          'The fields were updated in {{product}}. The following new required fields without default values were found: {{fieldNames}}.',
          {
            product: getIntegrationProductInfoFromSlug(
              integrationProductSlug as unknown as ProductSlugs
            ).displayName,
            fieldNames: newlyAddedRequiredFieldsWithoutDefault
              .map(({ name }) => `'${name}'`)
              .join(', ')
          }
        );
      }

      if (missedFields.length) {
        missedFields.forEach(field => {
          const mapperForThisField = mappers.find(mapper => {
            const fieldEntity = issueMappingFields.find(
              ({ key }) => key === field.key
            );

            return fieldEntity && mapper.canMapThisField(fieldEntity);
          });

          const sourceType = mapperForThisField?.isEnum
            ? IssueFieldSource.FixedValue
            : IssueFieldSource.Axe;

          initialValues.fieldMapping.push({
            axeField: '',
            mappingField: field.key,
            sourceType
          });
        });
      }

      //check if initial template has mapped fields that cannot be mapped with updated mapped logic
      if (initialTemplateMappedFields.length > formattedMappedFields.length) {
        const unmapped = initialTemplateMappedFields.reduce<string[]>(
          (fields, field) => {
            if (!formattedMappedFields.includes(field)) {
              const fieldEntity = findJiraFieldByKey(issueMappingFields, field);
              fields.push(`'${fieldEntity?.name}'`);
            }

            return fields;
          },
          []
        );

        const previousWarningMessage = warning && `${warning} `;

        warning = t(
          '{{previousWarningMessage}}The template has mapped fields that cannot be mapped: {{fieldNames}}. These fields will be skipped.',
          {
            fieldNames: unmapped.join(', '),
            previousWarningMessage
          }
        );
      }

      if (warning) {
        setContents(warning, 'caution');
      }

      reset(initialValues);
    } catch (error) {
      setContents((error as Error)?.message, 'error');
      history.push(`/configuration/integrations/${connectionId}/templates`);
    }
  }, [initialTemplate, issueMappingFields]);

  const mapFieldMappingFormValues = useCallback(() => {
    const formFieldMapping = getValues(
      'fieldMapping'
    ) as unknown as FieldMappingField[];

    const filteredFieldMapping = formFieldMapping.reduce<FieldMappingField[]>(
      (result, field) => {
        if (field.axeField && field.mappingField) {
          result.push(field);
        }
        return result;
      },
      []
    );

    return AxeIssueMappingSchema.write(
      filteredFieldMapping,
      issueMappingFields || []
    );
  }, [issueMappingFields]);

  useEffect(() => {
    const { errors } = formState;

    if (!Object.keys(errors).length) {
      return;
    }

    const formFields = Object.keys(
      defaultTemplateValues
    ) as TemplateFormFieldsKeys[];

    const firstErrorFieldName = formFields.find(
      field => errors[field]
    ) as TemplateFormFieldsKeys;
    const firstError = errors[firstErrorFieldName];

    // for fieldMapping field
    if (!Array.isArray(firstError)) {
      setFocus(firstErrorFieldName);
      return;
    }

    const keyOfFirstErrorArrayItem =
      `${firstErrorFieldName}[${firstError.findIndex(
        item => !!item
      )}]` as TemplateFormFieldsKeys;
    const values = getValues(keyOfFirstErrorArrayItem);

    const result = Object.entries(values).find(([, value]) => !value);
    setFocus(
      `${keyOfFirstErrorArrayItem}.${result?.[0]}` as TemplateFormFieldsKeys
    );
  }, [formState, setFocus]);

  const onSubmit = async () => {
    setIsLoading(true);

    const {
      templateName,
      issueType,
      project,
      impactMapping,
      attachScreenshot,
      summaryPrefix
    } = getValues();

    const { displayName: integration } = getIntegrationProductInfoFromSlug(
      integrationProductSlug as unknown as ProductSlugs
    );
    const projectEntity = projects.find(p => p.id === project);
    const projectName = projectEntity?.name || '';
    const issueTypeName =
      projectEntity?.issueTypes?.find(i => i.id === issueType)?.name || '';

    const analyticsPayload = {
      integration: integrationProductSlug,
      project: projectName,
      issueType: issueTypeName,
      template: templateName,
      connectionId,
      enterpriseId
    };

    try {
      const fieldMapping = mapFieldMappingFormValues();
      const templateData = {
        name: templateName,
        issue_type_id: issueType,
        project_id: project,
        attach_screenshot: attachScreenshot,
        field_mappings: fieldMapping,
        impact_mappings: impactMapping,
        summary_prefix: summaryPrefix
      };

      const result = initialTemplate
        ? await updateIntegrationTemplate(
            token,
            enterpriseId,
            connectionId,
            initialTemplate.id,
            templateData
          )
        : await createIntegrationTemplate(token, enterpriseId, connectionId, {
            ...templateData,
            product_slug: integrationProductSlug
          });

      refreshTemplates();

      if (initialTemplate) {
        analytics.integrationTemplateEditSave(analyticsPayload);
      } else {
        analytics.integrationTemplateAddSave(analyticsPayload);
      }

      const toastMessage = t(
        'Successfully {{ appliedAction }} {{ integration }} Template "{{ templateName }}"',
        {
          appliedAction: initialTemplate ? t('updated') : t('added'),
          integration,
          templateName: result.name
        }
      );

      setContents(toastMessage, 'confirmation');
      setIsLoading(false);

      return history.push(
        `/configuration/integrations/${connectionId}/templates/`
      );
    } catch {
      setIsLoading(false);

      if (initialTemplate) {
        analytics.integrationTemplateEditFail(analyticsPayload);
      } else {
        analytics.integrationTemplateAddFail(analyticsPayload);
      }

      const toastMessage = t(
        'Unable to {{ appliedAction }} a {{ integration }} Template "{{ templateName }}"',
        {
          appliedAction: initialTemplate ? 'update' : 'create',
          integration,
          templateName
        }
      );
      setContents(toastMessage, 'error');
    }
  };

  const onInvalidDataSubmit = useCallback(() => {
    setContents(
      t(
        'One or more steps are filled incorrectly. Please revise and try again.'
      ),
      'error'
    );
  }, [t]);

  if (isLoading) {
    const loaderLabel = initialTemplate
      ? t('Updating template...')
      : t('Creating template...');

    return <Loader label={loaderLabel} />;
  }

  return (
    <form className={styles.wrap} ref={formRefHandler}>
      <Panel padding={false} className={styles.container}>
        <PanelContent
          className={classNames(styles.footerContentContainer, {
            [styles.mobilePanelContent]: narrow
          })}
        >
          <div className={styles.headerContainer}>
            <h1 className={styles.header}>{steps[currentStep].header}</h1>
            {steps[currentStep].description && (
              <p className={styles.description}>
                {steps[currentStep].description}
              </p>
            )}
          </div>
          {steps[currentStep].content}
        </PanelContent>
        <TemplateWizardFooter
          integrationProductSlug={integrationProductSlug}
          connectionId={connectionId}
          handleSubmit={handleSubmit(onSubmit, onInvalidDataSubmit)}
          narrow={narrow}
          isEditMode={!!initialTemplate}
          steps={steps}
          currentStep={currentStep}
          isStepDataLoading={isStepDataLoading}
          isStepDataError={isStepDataError}
          setCurrentStep={setCurrentStep}
          templates={templates}
          projects={projects}
          enterpriseId={enterpriseId}
        />
      </Panel>
    </form>
  );
};

export default TemplateWizardForm;
